'use client';

import ExpandImageIcon from 'assets/icons/expand-image.svg';
import DownloadIcon from 'assets/icons/download.svg';
import ShopIcon from 'assets/icons/shop.svg';
import Icon3D from 'assets/icons/3D.svg';
import OpenAccessToolTip from 'components/collection/open-access-tooltip';
import DownloadMenuButton, {
	type DownloadMenuItem,
} from 'components/collection/download-menu-button';
import styles from './image-buttons.module.scss';
import { type MouseEventHandler } from 'react';
import SocialShare from 'components/social-share';
import IconButton from 'components/controls/buttons/icon-button';
import { type IVntanaAsset } from '@metmuseum/types';
import type { ImageViewerAnalyticsEvent } from '..';
import { getVntanaDownloadOptions } from 'lib/vntana/downloads';
import { humanizeFileSize } from 'helpers/helpers';

const ImageButtons = ({
	onFeaturedImageClick,
	downloadUrl,
	isOpenAccess,
	objectID,
	printUrl,
	vntanaAssets,
	thumbnailUrl,
	toggle3DImage,
	title,
	onAnalyticsEvent,
}: Props) => {
	const vntanaDownloadOptions = getVntanaDownloadOptions(
		vntanaAssets?.[0],
		isOpenAccess,
	);
	const getImageExtension = (url: string) => {
		try {
			return (
				'.' + (new URL(url).pathname.split('.').pop()?.toLowerCase() ?? 'jpg')
			);
		} catch {
			return '.jpg';
		}
	};

	const handleViewIn3DClick: MouseEventHandler = (event) => {
		onAnalyticsEvent?.({ type: 'view_in_3d' });
		toggle3DImage?.(event);
	};

	const handleDownloadImageClick = () => {
		onAnalyticsEvent?.({ type: 'download_image' });
		window.open(downloadUrl, '_blank');
	};

	const handleBuyPrintClick = () => {
		onAnalyticsEvent?.({ type: 'buy_a_print' });
		window.open(printUrl, '_blank');
	};

	const downloadItems: DownloadMenuItem[] = [
		...(isOpenAccess
			? [
					{
						href: downloadUrl,
						id: 'image-download',
						label: 'Download image',
						meta: getImageExtension(downloadUrl),
						onClick: () => onAnalyticsEvent?.({ type: 'download_image' }),
					},
				]
			: []),
		...vntanaDownloadOptions.map((option) => ({
			href: option.url,
			id: option.format,
			label: 'Download 3D model',
			meta: `${option.extension}, ${humanizeFileSize(option.sizeBytes, 1)}`,
			onClick: () => onAnalyticsEvent?.({ type: 'download_3d_model' }),
		})),
	];

	return (
		<div className={styles.container}>
			{isOpenAccess && (
				<span className={styles.oaWrapper}>
					<OpenAccessToolTip />
				</span>
			)}
			<div className={styles.buttonsWrapper}>
				{vntanaAssets && vntanaAssets.length > 0 && (
					<IconButton
						icon={<Icon3D />}
						data-testid="three-d-button"
						label="View in 3D"
						lightMode={true}
						small={true}
						showLabel={true}
						onClick={handleViewIn3DClick}
					/>
				)}
				{vntanaDownloadOptions.length > 0 ? (
					<DownloadMenuButton
						buttonLabel="Download"
						iconOnly={true}
						lightMode={true}
						items={downloadItems}
						small={true}
					/>
				) : (
					isOpenAccess && (
						<IconButton
							label="Download Image"
							lightMode={true}
							small={true}
							icon={<DownloadIcon />}
							onClick={handleDownloadImageClick}
						/>
					)
				)}
				{printUrl && (
					<IconButton
						label="Buy a print"
						lightMode={true}
						small={true}
						showLabel={true}
						icon={<ShopIcon />}
						onClick={handleBuyPrintClick}
					/>
				)}
				{objectID && (
					<SocialShare
						objectID={objectID}
						thumbnail={thumbnailUrl}
						title={title}
					/>
				)}
				{isOpenAccess && (
					<IconButton
						icon={<ExpandImageIcon />}
						lightMode={true}
						small={true}
						label="Enlarge Image"
						onClick={onFeaturedImageClick}
						className={styles.expandImageIcon}
					/>
				)}
			</div>
		</div>
	);
};

export default ImageButtons;

interface Props {
	onFeaturedImageClick: MouseEventHandler;
	downloadUrl: string;
	isOpenAccess: boolean;
	objectID?: string | number;
	printUrl?: string;
	vntanaAssets?: IVntanaAsset[] | null;
	thumbnailUrl: string;
	title: string;
	toggle3DImage?: MouseEventHandler;
	onAnalyticsEvent?: (event: ImageViewerAnalyticsEvent) => void;
}
